import React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import type { ClosedPositionData } from "@crypto-trading/common/browser";
import { CoinLogo, coinLogoBase } from "@/components/CoinLogo";
import { ExchangeLogo } from "@/components/ui/ExchangeLogo";
import { exchangeBrand } from "@/lib/exchange-brand";
import { formatSignedPercent, formatSignedUsd } from "@/lib/position-protection";

const closedAtLabel = (iso: string | null) => {
  if (!iso) return "–";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${date} ${time}`;
};

const size = (p: ClosedPositionData) =>
  `${Number(p.amount).toLocaleString("en-US", { maximumFractionDigits: 4 })} ${coinLogoBase(p.symbol)}`;

/** $3,120.50 above $1, else four significant digits ($0.07820, $0.0001234). */
const price = (value: number | null) => {
  if (value === null || value === undefined) return "–";
  const v = Number(value);
  if (Math.abs(v) >= 1) {
    return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `$${v.toPrecision(4)}`;
};

const exchangeName = (p: ClosedPositionData) =>
  p.exchangeName || exchangeBrand(p.exchangeCode).name;

export const SidePill: React.FC<{ side: string }> = ({ side }) => {
  const long = side.toUpperCase() === "LONG";
  return (
    <span
      className={`inline-flex rounded-md border px-1.5 py-px text-xs font-medium leading-[18px] ${
        long
          ? "border-[#085d3a] bg-[#053321] text-[#2dcc89]"
          : "border-[#7a271a] bg-[#55160c] text-[#f97066]"
      }`}
    >
      {long ? "Long" : "Short"}
    </span>
  );
};

const PnlPill: React.FC<{ value: number | null; children: React.ReactNode }> = ({ value, children }) => {
  const v = Number(value ?? 0);
  const cls =
    v > 0
      ? "border-[#085d3a] bg-[#053321] text-[#2dcc89]"
      : v < 0
        ? "border-[#7a271a] bg-[#55160c] text-[#f97066]"
        : "border-white/10 bg-white/5 text-[#d4d4d4]";
  return (
    <span className={`inline-flex rounded-full border px-2 py-px text-xs font-medium leading-[18px] ${cls}`}>
      {children}
    </span>
  );
};

interface ListProps {
  positions: ClosedPositionData[];
  /** Row number of the first item (page offset). */
  offset: number;
  onSelect: (position: ClosedPositionData) => void;
}

/** Desktop table: # · Asset · Side · Size · Entry · Exit · PnL · PnL % · Closed At · Exchange. */
export const ClosedPositionsTable: React.FC<ListProps> = ({ positions, offset, onSelect }) => (
  <table className="w-full text-left">
    <thead>
      <tr className="border-y border-white/10 bg-white/[0.02] text-xs font-semibold text-[#a3a3a3]">
        {["#", "Assets", "Side", "Size", "Entry Price", "Exit Price", "PnL", "PnL %", "Closed At", "Exchange"].map(
          (h) => (
            <th key={h} className="whitespace-nowrap px-4 py-3 font-semibold first:pl-6">
              {h}
            </th>
          )
        )}
      </tr>
    </thead>
    <tbody>
      {positions.map((p, i) => (
        <tr
          key={p.id}
          onClick={() => onSelect(p)}
          className="cursor-pointer border-b border-white/[0.06] text-sm text-[#fafafa] transition-colors last:border-b-0 hover:bg-white/[0.03]"
        >
          <td className="px-4 py-4 pl-6 text-[#d4d4d4]">{offset + i + 1}</td>
          <td className="px-4 py-4">
            <span className="flex items-center gap-3 font-medium">
              <CoinLogo symbol={p.symbol} className="size-8" />
              {coinLogoBase(p.symbol)}
            </span>
          </td>
          <td className="px-4 py-4">
            <SidePill side={p.positionType} />
          </td>
          <td className="whitespace-nowrap px-4 py-4">{size(p)}</td>
          <td className="whitespace-nowrap px-4 py-4">{price(p.entryPrice)}</td>
          <td className="whitespace-nowrap px-4 py-4">{price(p.exitPrice)}</td>
          <td className="px-4 py-4">
            <PnlPill value={p.realizedPnl}>{formatSignedUsd(Number(p.realizedPnl ?? 0))}</PnlPill>
          </td>
          <td className="px-4 py-4">
            <PnlPill value={p.pnlPercentage}>{formatSignedPercent(Number(p.pnlPercentage ?? 0), 2)}</PnlPill>
          </td>
          <td className="whitespace-nowrap px-4 py-4">{closedAtLabel(p.closedAt)}</td>
          <td className="px-4 py-4">
            <span className="flex items-center gap-2 whitespace-nowrap">
              <ExchangeLogo code={p.exchangeCode ?? ""} size={28} />
              {exchangeName(p)}
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

/** Mobile cards: asset, side and size on top, PnL right, then entry / exit / closed at. */
export const ClosedPositionCards: React.FC<ListProps> = ({ positions, onSelect }) => (
  <div className="flex flex-col gap-3">
    {positions.map((p) => {
      const pnl = Number(p.realizedPnl ?? 0);
      const color = pnl > 0 ? "text-[#2dcc89]" : pnl < 0 ? "text-[#f97066]" : "text-[#d4d4d4]";
      return (
        <button
          key={p.id}
          type="button"
          onClick={() => onSelect(p)}
          className="flex flex-col gap-3 rounded-xl border border-white/10 bg-[#0d121a] p-3 text-left"
        >
          <div className="flex items-center gap-3 border-b border-white/[0.06] pb-3">
            <CoinLogo symbol={p.symbol} className="size-8" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">{coinLogoBase(p.symbol)}</p>
              <p className="text-xs text-[#a3a3a3]">{exchangeName(p)}</p>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <SidePill side={p.positionType} />
              <span className="text-xs font-semibold text-white">{size(p)}</span>
            </div>
            <div className="flex flex-col items-end">
              <span className={`text-base font-semibold ${color}`}>{formatSignedUsd(pnl)}</span>
              <span className={`text-xs ${color}`}>{formatSignedPercent(Number(p.pnlPercentage ?? 0), 2)}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 text-xs">
            {[
              ["Entry Price", price(p.entryPrice)],
              ["Exit Price", price(p.exitPrice)],
              ["Closed At", closedAtLabel(p.closedAt)],
            ].map(([label, value], i) => (
              <div key={label} className={`flex flex-col gap-0.5 ${i > 0 ? "border-l border-white/10 pl-2" : ""}`}>
                <span className="text-[#a3a3a3]">{label}</span>
                <span className="font-medium text-white">{value}</span>
              </div>
            ))}
          </div>
        </button>
      );
    })}
  </div>
);

interface PagerProps {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
  /** Numbered buttons on desktop, "Page n of m" on phones. */
  compact?: boolean;
}

/** Page numbers around the current one, with gaps: 1 2 3 … 8 9 10. */
function pageItems(page: number, count: number): Array<number | "gap"> {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const keep = new Set([1, 2, 3, count - 2, count - 1, count, page - 1, page, page + 1]);
  const items: Array<number | "gap"> = [];
  for (let n = 1; n <= count; n++) {
    if (keep.has(n)) items.push(n);
    else if (items[items.length - 1] !== "gap") items.push("gap");
  }
  return items;
}

export const Pager: React.FC<PagerProps> = ({ page, pageCount, onChange, compact }) => {
  const navBtn =
    "flex items-center gap-2 rounded-lg border border-white/10 bg-[#0d121a] px-3.5 py-2 text-sm font-semibold text-[#d4d4d4] transition-colors hover:border-white/25 disabled:cursor-not-allowed disabled:opacity-40";
  if (compact) {
    return (
      <div className="flex items-center justify-between">
        <button type="button" className={`${navBtn} !px-2`} aria-label="Previous page" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          <ArrowLeft className="size-5" />
        </button>
        <span className="text-sm font-medium text-[#d4d4d4]">
          Page {page} of {pageCount}
        </span>
        <button type="button" className={`${navBtn} !px-2`} aria-label="Next page" disabled={page >= pageCount} onClick={() => onChange(page + 1)}>
          <ArrowRight className="size-5" />
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between">
      <button type="button" className={navBtn} disabled={page <= 1} onClick={() => onChange(page - 1)}>
        <ArrowLeft className="size-5" />
        Previous
      </button>
      <div className="flex items-center gap-0.5">
        {pageItems(page, pageCount).map((item, i) =>
          item === "gap" ? (
            <span key={`gap-${i}`} className="flex size-10 items-center justify-center text-sm text-[#a3a3a3]">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              onClick={() => onChange(item)}
              aria-current={item === page ? "page" : undefined}
              className={`flex size-10 items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                item === page ? "bg-[#07a66c] text-white" : "text-[#a3a3a3] hover:bg-white/5"
              }`}
            >
              {item}
            </button>
          )
        )}
      </div>
      <button type="button" className={navBtn} disabled={page >= pageCount} onClick={() => onChange(page + 1)}>
        Next
        <ArrowRight className="size-5" />
      </button>
    </div>
  );
};
